<script>
  import { v4 } from "uuid";
  import debounce from "lodash/debounce";
  import { SEARCH } from "../../Utils/utilConstants.svelte";

  export let value; // keep this as your committed numeric value
  export let inputClass = "";
  export let inputStep = 0.01;
  export let inputMin = 0;
  export let inputMax;
  export let inputPlaceholder = "";
  export let isDisabled = false;
  export let inputFunc = () => {};
  export let onFocus = (e) => {};
  export let onBlur = (e) => {};
  export let debounceTime = SEARCH.BILLING_RULE_AMOUNT_TIME;

  let dispplayValue = "0.00";
  let raw = "";               // <-- what user is typing while focused (string)
  let focused = false;
  let isInvalid = false;      // <-- controls border
  const uuid = v4();

  const formatOptions = {
    maximumFractionDigits: 2,
    currency: "USD",
    style: "currency",
    currencyDisplay: "symbol",
  };

  // Regex allows intermediate states: "", "-", ".", "-.", "123.", "0.", "12.3"
  const isPotentialDecimal = (s) => /^-?\d*(\.\d*)?$/.test(s);

  const toNumberIfValid = (s) => {
    if (!s) return { ok: false };
    // Disallow intermediate endings that shouldn't be committed
    if (s === "-" || s === "." || s === "-." || s.endsWith(".")) return { ok: false };

    const n = Number(s);
    if (Number.isNaN(n)) return { ok: false };
    return { ok: true, n };
  };

  const clamp = (n) => {
    if (inputMin !== undefined && n < inputMin) n = inputMin;
    if (inputMax !== undefined && n > inputMax) n = inputMax;
    return n;
  };

  const formatDisplayFromValue = (v) => {
    if (v === null || v === undefined || v === "" || Number.isNaN(v)) return "0.00";
    const n = Number(v);
    return n
      .toLocaleString(undefined, formatOptions)
      .replace(/[^0-9,.-]+/g, "");
  };

  // Update display when NOT focused (don’t overwrite what user types while focused)
  $: if (!focused) {
    dispplayValue = formatDisplayFromValue(value);
  }

  const commitIfValid = () => {
    const { ok, n } = toNumberIfValid(raw);
    if (!ok) {
      isInvalid = raw !== ""; // show invalid only if something typed
      return;
    }

    isInvalid = false;
    let next = clamp(n);
    next = Math.round(next * 100) / 100;

    // keep raw in sync with clamped/rounded committed value (optional)
    raw = String(next);

    value = next;
    inputFunc();
  };

  const onNumberInputType = debounce(() => {
    // Only commit when valid; otherwise keep raw as-is and mark invalid.
    commitIfValid();
  }, debounceTime);

  const onNumberInputInput = (e) => {
    const next = e.target.value; // string
    // allow only characters that can form a decimal
    if (!isPotentialDecimal(next)) return;

    raw = next;
    // mark invalid immediately for intermediate states like "123."
    const { ok } = toNumberIfValid(raw);
    isInvalid = raw !== "" && !ok;

    onNumberInputType();
  };

  const onNumberInputPaste = (e) => {
    const pasteData = e.clipboardData.getData("text");
    const sanitizedData = pasteData.replace(/[^0-9.-]+/g, "");
    e.preventDefault();

    if (!isPotentialDecimal(sanitizedData)) return;

    raw = sanitizedData;
    commitIfValid(); // try commit immediately
  };

  const onNumberInputKeyDown = (e) => {
    const key = e.key;
    if (key.length === 1 && !key.match(/^[0-9.\-]+$/) && !e.ctrlKey && !e.metaKey)
      e.preventDefault();

    // only one dot
    if (key === "." && raw.includes(".")) e.preventDefault();

    // only one leading minus
    if (key === "-" && (raw.includes("-") || e.target.selectionStart !== 0))
      e.preventDefault();
  };

  const onNumberInputBlur = () => {
    focused = false;

    // On blur: if it's "123." treat as invalid and DON'T overwrite value
    commitIfValid();

    const numberInput = document.getElementById(uuid);
    if (numberInput) {
      onBlur(numberInput);
    }

    // If invalid, keep the last committed value in display (or choose to show raw)
    // Current behavior: leave display from value (since focused becomes false)
  };

  const onTextInputFocused = () => {
    focused = true;
    isInvalid = false;

    // initialize raw from committed numeric value
    raw = (value ?? value === 0) ? String(Math.round(Number(value) * 100) / 100) : "";

    setTimeout(() => {
      const numberInput = document.getElementById(uuid);
      if (numberInput) {
        numberInput.focus();
        onFocus(numberInput);
      }
    }, 10);
  };
</script>

{#if focused}
  <input
    type="text"
    inputmode="decimal"
    class={`input input-sm input-bordered ${inputClass} ${isInvalid ? "input-error" : ""}`}
    disabled={isDisabled}
    id={uuid}
    value={raw}
    on:input={onNumberInputInput}
    on:keydown={onNumberInputKeyDown}
    on:blur={onNumberInputBlur}
    on:paste={onNumberInputPaste}
  />
{:else}
  <input
    type="text"
    class={`input input-sm input-bordered ${inputClass}`}
    placeholder={inputPlaceholder}
    disabled={isDisabled}
    bind:value={dispplayValue}
    on:focus|preventDefault={onTextInputFocused}
  />
{/if}
